import { createContext, use, type ReactNode } from 'react'

import type { AuthMode } from '@/shared/config/runtime-config'

import type { SiteSignInOptions } from './site-sign-in-options'

/** Who is signed in, for display only. Nothing is keyed on any of it. */
export interface CurrentUser {
  /** A name to greet somebody by: the first and last name TMS stores, else the token's name or username. */
  readonly displayName: string
}

/** Everything the rest of the app needs to know about the signed-in caller. */
export interface AuthState {
  /** Which mode the deployment is in. Read it to explain, never to decide access. */
  readonly mode: AuthMode
  /** While true (MSAL starting up or signing out), {@link isAuthenticated} is not final. */
  readonly isResolving: boolean
  /** Whether the caller is signed in to B2C. The site's session follows (`useSession`). */
  readonly isAuthenticated: boolean
  /** The signed-in caller, or `null` until the site's session has started. */
  readonly user: CurrentUser | null
  /**
   * Starts sign-in with `login_hint`/`domain_hint` from the address, or the site's own domain hint
   * or B2C policy when `site` names one. Navigates away on success.
   */
  readonly signIn: (email: string, site?: SiteSignInOptions | null) => Promise<void>
  /**
   * Ends the session, signs out and returns to the login page, by way of the registered callback.
   * `isSessionEnded` skips ending the session when the API has already ended it, as closing an account does.
   */
  readonly signOut: (options?: { readonly isSessionEnded?: boolean }) => Promise<void>
  /**
   * Forgets a sign-in the site refused, in this browser only and without leaving the page: no
   * account or token is kept, and the next sign-in asks B2C for the person's credentials again.
   */
  readonly forgetSignIn: () => Promise<void>
  /**
   * An API access token, or `null` when nobody is signed in or authentication is disabled. When B2C
   * needs the person to sign in again, it starts that sign-in, unless `isSilent`: then it throws, as
   * for a check the app makes on its own, which must never take the person off the page. On a public
   * page it gives `null` instead, so a read goes signed out; for an `isWrite` it throws a
   * `SignInAgainError`, so no write goes signed out.
   */
  readonly getAccessToken: (options?: {
    readonly isForcedRefresh?: boolean
    readonly isSilent?: boolean
    readonly isWrite?: boolean
  }) => Promise<string | null>
}

const AuthContext = createContext<AuthState | null>(null)

/** Reads the authentication state. Throws outside `AuthProvider`. */
export function useAuth(): AuthState {
  const state = use(AuthContext)
  if (state === null) {
    throw new Error('useAuth must be used inside an AuthProvider.')
  }
  return state
}

/** Supplies the authentication state; only `AuthProvider` and the session provider inside it use it. */
export function AuthStateProvider(props: { readonly value: AuthState; readonly children: ReactNode }) {
  return <AuthContext value={props.value}>{props.children}</AuthContext>
}
